import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarRange, Users, User, Download, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { STATUS_META } from "@/constants/status";

// ---------------------------------------------------------------------------
// MonthlyReport — on-screen view of each person's daily-log entries over a
// chosen month / custom date range.  Managers (+ admins + super admins) only.
// Scope is either a single individual or a whole team.
// Backed by `/api/reports/work-tracking` which already returns a per-user
// day-by-day ledger with hours, status, OT etc.  For a team view we loop the
// endpoint per teammate (usually ≤ 8 members per team).
// ---------------------------------------------------------------------------

const TEAMS = ["Edit Team", "Digital Team", "Shoot Team", "Management"];

const STATUS_COLORS = {
  ...Object.fromEntries(Object.entries(STATUS_META).map(([k, v]) => [k, v.pill.replace(/\bborder border-/, "border-")])),
  leave: "text-rose-300 bg-rose-500/10 border-rose-500/30",
  weekly_off: "text-white/40 bg-white/5 border-white/10",
  holiday: "text-sky-300 bg-sky-500/10 border-sky-500/30",
};

function statusLabel(s) {
  if (!s) return "—";
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function todayIst() {
  const d = new Date();
  d.setMinutes(d.getMinutes() + d.getTimezoneOffset() + 330); // IST shift
  return d;
}

function monthRange(ym) {
  // Normalise YYYY-M / YYYY-MM so both halves of the range have a zero-padded
  // month. Some browsers return a one-digit month from <input type="month">,
  // which quietly bypassed the DB string match ("2026-09-01" vs "2026-9-01").
  const [yStr, mStr] = ym.split("-");
  const y = Number(yStr);
  const m = Number(mStr);
  const pad = String(m).padStart(2, "0");
  const last = new Date(y, m, 0).getDate(); // 0-th of next month = last of this
  return [`${y}-${pad}-01`, `${y}-${pad}-${String(last).padStart(2, "0")}`];
}

function fmtDmy(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y.slice(2)}`;
}

export default function MonthlyReport() {
  const { user } = useAuth();
  const isMgrPlus = user && (user.role === "manager" || user.is_admin || user.is_super_admin);

  // ---- Pickers ----
  const now = todayIst();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [mode, setMode] = useState("month"); // "month" | "range"
  const [month, setMonth] = useState(defaultMonth);
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");

  const [scope, setScope] = useState("individual"); // "individual" | "team"
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedTeam, setSelectedTeam] = useState("");
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]); // [{user, rows, summary}]

  const dateFrom = mode === "month" ? monthRange(month)[0] : rangeFrom;
  const dateTo = mode === "month" ? monthRange(month)[1] : rangeTo;

  // ---- Users list (scoped by caller RBAC — the backend already handles that) ----
  useEffect(() => {
    if (!isMgrPlus) return;
    api
      .get("/users")
      .then(({ data }) => {
        const sorted = (data || [])
          .slice()
          .sort((a, b) => (a.team || "").localeCompare(b.team || "") || a.name.localeCompare(b.name));
        setUsers(sorted);
        if (sorted.length && !selectedUserId) setSelectedUserId(sorted[0].id);
      })
      .catch(() => toast.error("Failed to load users"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMgrPlus]);

  // Default team pick for team scope — caller's own team if they have one, else first.
  useEffect(() => {
    if (!selectedTeam && user?.team) setSelectedTeam(user.team);
  }, [user, selectedTeam]);

  const run = useCallback(async (override) => {
    const effDateFrom = override?.dateFrom ?? dateFrom;
    const effDateTo = override?.dateTo ?? dateTo;
    if (!effDateFrom || !effDateTo) {
      toast.error("Pick a date range");
      return;
    }
    if (effDateFrom > effDateTo) {
      toast.error("'From' must be on or before 'To'");
      return;
    }
    setLoading(true);
    setReports([]);
    try {
      // Resolve which user ids to pull.
      let targetIds = [];
      let targetUsers = [];
      if (scope === "individual") {
        if (!selectedUserId) {
          toast.error("Pick a teammate");
          return;
        }
        const u = users.find((x) => x.id === selectedUserId);
        targetIds = [selectedUserId];
        targetUsers = u ? [u] : [{ id: selectedUserId, name: "Member" }];
      } else {
        const members = users.filter((x) => x.team === selectedTeam);
        if (members.length === 0) {
          toast.error(`No teammates in ${selectedTeam || "selected team"}`);
          return;
        }
        targetIds = members.map((m) => m.id);
        targetUsers = members;
      }
      // Parallel fetch — one request per teammate. Fast enough for a team of 10
      // and keeps the backend unchanged for this iteration.
      const results = await Promise.all(
        targetIds.map((uid) =>
          api
            .get("/reports/work-tracking", { params: { date_from: effDateFrom, date_to: effDateTo, user_id: uid } })
            .then((r) => r.data)
            .catch((e) => ({ __error: formatApiError(e.response?.data?.detail) || "Load failed", user_id: uid }))
        )
      );
      const packed = results.map((r, i) => ({ user: targetUsers[i], ...r }));
      setReports(packed);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, scope, selectedUserId, selectedTeam, users]);

  // Auto-run on first paint so the page isn't blank.
  useEffect(() => {
    if (!isMgrPlus) return;
    if (users.length === 0) return;
    if (scope === "individual" && !selectedUserId) return;
    if (scope === "team" && !selectedTeam) return;
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMgrPlus, users.length]);

  const [downloading, setDownloading] = useState(false);
  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (scope === "individual") params.user_id = selectedUserId;
      else params.team = selectedTeam;
      const res = await api.get("/reports/work-tracking-pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const label = scope === "individual" ? (users.find((u) => u.id === selectedUserId)?.name || "member") : selectedTeam;
      a.download = `LIM-Monthly-${label.replace(/\W+/g, "-")}-${dateFrom}-to-${dateTo}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Monthly report downloaded");
    } catch {
      toast.error("Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  const grandTotals = useMemo(() => {
    let hours = 0;
    let tasks = 0;
    let completed = 0;
    let leaves = 0;
    reports.forEach((r) => {
      if (!r.summary) return;
      hours += (r.summary.total_seconds || 0) / 3600;
      tasks += r.summary.total || 0;
      completed += r.summary.complete || 0;
      leaves += (r.days || []).filter((d) => d.state === "leave").length;
    });
    return { hours, tasks, completed, leaves };
  }, [reports]);

  const pullLast90 = useCallback(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 90);
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const from = iso(start);
    const to = iso(end);
    setMode("range");
    setRangeFrom(from);
    setRangeTo(to);
    // Run with explicit overrides — state updates haven't flushed yet so
    // `run()` on its own would use the previous (empty) range values.
    run({ dateFrom: from, dateTo: to });
  }, [run]);

  if (!user) return null;
  if (!isMgrPlus) return <Navigate to="/" replace />;

  return (
    <div className="p-4 sm:p-6" data-testid="monthly-report-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="overline flex items-center gap-2"><CalendarRange size={14} /> Monthly Report</div>
          <h1 className="text-3xl sm:text-4xl font-display tracking-tight mt-1">Work done · {scope === "individual" ? "Individual" : "Team"}</h1>
          <p className="text-sm text-white/50 mt-1">Everything each person logged — tasks, extras, hours, status — stitched together from their daily logs.</p>
        </div>
        <Button
          onClick={downloadPdf}
          disabled={loading || downloading || !reports.length}
          className="bg-white/10 hover:bg-white/15 text-sm h-10"
          data-testid="monthly-report-download"
        >
          {downloading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Download size={14} className="mr-1.5" />} Download PDF
        </Button>
      </div>

      {/* Pickers */}
      <div className="glass rounded-xl p-4 mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Date mode */}
          <div>
            <div className="overline mb-1.5">Period</div>
            <div className="flex items-center gap-1 bg-black/40 border border-white/10 rounded-md p-1">
              <button
                type="button"
                onClick={() => setMode("month")}
                className={`flex-1 h-8 rounded text-sm transition ${mode === "month" ? "bg-[#E11D2E] text-white" : "text-white/60 hover:text-white"}`}
                data-testid="monthly-report-mode-month"
              >
                Month
              </button>
              <button
                type="button"
                onClick={() => setMode("range")}
                className={`flex-1 h-8 rounded text-sm transition ${mode === "range" ? "bg-[#E11D2E] text-white" : "text-white/60 hover:text-white"}`}
                data-testid="monthly-report-mode-range"
              >
                Custom
              </button>
            </div>
          </div>

          {/* Date values */}
          {mode === "month" ? (
            <div>
              <div className="overline mb-1.5">Month</div>
              <Input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="bg-black/40 border-white/10 h-10 text-sm"
                data-testid="monthly-report-month"
              />
            </div>
          ) : (
            <>
              <div>
                <div className="overline mb-1.5">From</div>
                <Input type="date" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)} className="bg-black/40 border-white/10 h-10 text-sm" data-testid="monthly-report-range-from" />
              </div>
              <div>
                <div className="overline mb-1.5">To</div>
                <Input type="date" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} className="bg-black/40 border-white/10 h-10 text-sm" data-testid="monthly-report-range-to" />
              </div>
            </>
          )}

          {/* Scope */}
          <div>
            <div className="overline mb-1.5">Scope</div>
            <div className="flex items-center gap-1 bg-black/40 border border-white/10 rounded-md p-1">
              <button
                type="button"
                onClick={() => setScope("individual")}
                className={`flex-1 h-8 rounded text-sm transition inline-flex items-center justify-center gap-1 ${scope === "individual" ? "bg-[#E11D2E] text-white" : "text-white/60 hover:text-white"}`}
                data-testid="monthly-report-scope-individual"
              >
                <User size={13} /> Individual
              </button>
              <button
                type="button"
                onClick={() => setScope("team")}
                className={`flex-1 h-8 rounded text-sm transition inline-flex items-center justify-center gap-1 ${scope === "team" ? "bg-[#E11D2E] text-white" : "text-white/60 hover:text-white"}`}
                data-testid="monthly-report-scope-team"
              >
                <Users size={13} /> Team
              </button>
            </div>
          </div>

          {/* Target */}
          {scope === "individual" ? (
            <div className={mode === "month" ? "" : "sm:col-span-2 lg:col-span-1"}>
              <div className="overline mb-1.5">Teammate</div>
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="w-full bg-black/40 border border-white/10 rounded-md h-10 px-2 text-sm text-white"
                data-testid="monthly-report-user-select"
              >
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name} · {u.team || "—"}</option>
                ))}
              </select>
            </div>
          ) : (
            <div className={mode === "month" ? "" : "sm:col-span-2 lg:col-span-1"}>
              <div className="overline mb-1.5">Team</div>
              <select
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                className="w-full bg-black/40 border border-white/10 rounded-md h-10 px-2 text-sm text-white"
                data-testid="monthly-report-team-select"
              >
                {TEAMS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-4">
          <div className="text-xs text-white/40 font-mono">
            {dateFrom && dateTo ? `${fmtDmy(dateFrom)} → ${fmtDmy(dateTo)}` : "Pick a period"}
          </div>
          <Button
            onClick={run}
            disabled={loading}
            className="bg-[#E11D2E] hover:bg-[#F03041] text-sm h-10"
            data-testid="monthly-report-run"
          >
            {loading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <FileText size={14} className="mr-1.5" />}
            {loading ? "Pulling logs…" : "Generate"}
          </Button>
        </div>
      </div>

      {/* Grand totals (team only) */}
      {scope === "team" && reports.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <StatCard label="Members" value={reports.length} />
          <StatCard label="Logged hours" value={grandTotals.hours.toFixed(1) + " h"} />
          <StatCard label="Tasks touched" value={grandTotals.tasks} subtle={`${grandTotals.completed} completed`} />
          <StatCard label="Leaves" value={grandTotals.leaves} />
        </div>
      )}

      {/* Reports */}
      {reports.length === 0 && !loading && (
        <div className="glass rounded-xl p-10 text-center text-white/40 text-sm">
          Pick a period and scope, then hit Generate.
        </div>
      )}
      <div className="space-y-5">
        {reports.map((r) => (
          <UserReportCard key={r.user?.id || Math.random()} report={r} onPickLast90={pullLast90} />
        ))}
      </div>
    </div>
  );
}

function StatCard({ label, value, subtle }) {
  return (
    <div className="glass rounded-xl p-4">
      <div className="overline">{label}</div>
      <div className="text-2xl font-display mt-1">{value}</div>
      {subtle && <div className="text-xs text-white/40 font-mono mt-0.5">{subtle}</div>}
    </div>
  );
}

const DAY_STATE = {
  working: null,
  leave: { label: "On Leave", cls: "text-rose-300 bg-rose-500/10 border-rose-500/30" },
  weekly_off: { label: "Weekly Off", cls: "text-white/50 bg-white/5 border-white/15" },
  holiday: { label: "Holiday", cls: "text-sky-300 bg-sky-500/10 border-sky-500/30" },
  no_log: { label: "No Log", cls: "text-red-300 bg-red-500/15 border-red-500/40" },
  weekly_off_no_log: { label: "Weekly Off · No Log", cls: "text-white/40 bg-white/5 border-white/10" },
  upcoming: { label: "Upcoming", cls: "text-white/30 bg-white/[0.03] border-white/10" },
};

function weekday(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-IN", { weekday: "short" });
}

function UserReportCard({ report, onPickLast90 }) {
  const { user, days = [], summary = {}, __error } = report;
  const name = user?.name || "Teammate";
  const team = user?.team || "";
  const filed = days.filter((d) => d.has_log).length;
  const missing = days.filter((d) => d.state === "no_log").length;

  if (__error) {
    return (
      <div className="glass rounded-xl p-5 border border-rose-500/30 bg-rose-500/5">
        <div className="font-display text-lg">{name} <span className="text-sm text-white/50">· {team}</span></div>
        <div className="text-rose-300 text-sm mt-2">{__error}</div>
      </div>
    );
  }

  return (
    <div className="glass rounded-xl p-5" data-testid={`monthly-report-user-${user?.id}`}>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <div className="text-xl font-display">{name}</div>
          <div className="text-xs text-white/50 font-mono mt-0.5">
            {team || "No team"} · {filed} day{filed === 1 ? "" : "s"} filed
            {missing ? <span className="text-red-300"> · {missing} missing</span> : null}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Pill tone="emerald" label={`${summary.complete || 0} complete`} />
          <Pill tone="amber" label={`${(summary.total || 0) - (summary.complete || 0)} open`} />
          {summary.total_hours_label && <Pill tone="sky" label={summary.total_hours_label} />}
          {summary.ot_half_days ? <Pill tone="violet" label={`½ OT · ${summary.ot_half_days}d`} /> : null}
          {summary.ot_full_days ? <Pill tone="violet" label={`Full OT · ${summary.ot_full_days}d`} /> : null}
        </div>
      </div>

      {filed === 0 && onPickLast90 && (
        <div className="rounded-lg border border-dashed border-white/15 bg-black/20 px-4 py-3 mb-3 text-sm text-white/60 flex flex-wrap items-center justify-between gap-2">
          <span>No daily logs filed in this range.</span>
          <button type="button" onClick={onPickLast90}
            className="text-xs uppercase tracking-widest font-mono text-rose-300 hover:text-rose-200 hover:underline underline-offset-4"
            data-testid="monthly-report-empty-last-90">Pull last 90 days →</button>
        </div>
      )}

      <div className="space-y-3">
        {days.map((day) => <DayCard key={day.date} day={day} />)}
      </div>
    </div>
  );
}

function DayCard({ day }) {
  const st = DAY_STATE[day.state];
  const otLabel = day.overtime === "half" ? "½ Day OT" : day.overtime === "full" ? "Full Day OT" : "";
  const hasNotes = !!(day.summary && day.summary.trim());
  const dim = !day.has_log;
  return (
    <div className={`rounded-lg border bg-black/20 transition-colors ${day.state === "no_log" ? "border-red-500/30" : "border-white/10"} ${dim ? "opacity-70" : ""}`}
      data-testid={`monthly-report-day-${day.date}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-white/5 bg-white/[0.02]">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-sm font-mono font-medium">
            <span className="text-white/40 mr-1.5">{weekday(day.date)}</span>{fmtDmy(day.date)}
          </div>
          {st && (
            <span className={`text-[0.7rem] uppercase tracking-widest font-mono rounded-full border px-2 py-0.5 ${st.cls}`}
              data-testid={`monthly-report-day-state-${day.date}`}>{st.label}</span>
          )}
          {otLabel && (
            <span className="text-[0.7rem] uppercase tracking-widest font-mono rounded-full border px-2 py-0.5 text-cyan-300 bg-cyan-500/10 border-cyan-500/30">
              {otLabel}{day.overtime_client ? ` · ${day.overtime_client}` : ""}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs font-mono">
          {day.hours && <span className="text-sky-300" data-testid={`monthly-report-day-hours-${day.date}`}>{day.hours}</span>}
          {day.has_log && <span className="text-white/40">{day.items.length} task{day.items.length === 1 ? "" : "s"}</span>}
        </div>
      </div>

      {day.items.length > 0 && (
        <ul className="divide-y divide-white/5">
          {day.items.map((it, idx) => <TaskLine key={idx} it={it} />)}
        </ul>
      )}
      {day.has_log && day.items.length === 0 && !hasNotes && (
        <div className="px-3 py-2.5 text-sm text-white/40 italic">No task entries filed for this day.</div>
      )}
      {hasNotes && (
        <div className="px-3 py-2.5 border-t border-white/5 bg-white/[0.015]" data-testid={`monthly-report-summary-${day.date}`}>
          <div className="text-[0.7rem] uppercase tracking-widest text-white/40 font-mono mb-1">Daily notes</div>
          <div className="text-sm text-white/85 whitespace-pre-wrap leading-relaxed">{day.summary}</div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-[0.7rem] uppercase tracking-widest text-white/35 font-mono">{label}</div>
      <div className="text-sm text-white/85 truncate" title={value || ""}>{value || "—"}</div>
    </div>
  );
}

function TaskLine({ it }) {
  return (
    <li className="px-3 py-2.5 grid grid-cols-2 md:grid-cols-[1fr_1.3fr_2fr_8.5rem_5rem] gap-x-4 gap-y-2 items-center hover:bg-white/[0.02] transition-colors">
      <div className="min-w-0">
        <div className="text-[0.7rem] text-white/40">Client</div>
        <div className="text-sm truncate client-line" title={it.client || ""}>{it.client || "—"}</div>
      </div>
      <Field label="Project" value={it.project} />
      <div className="col-span-2 md:col-span-1 min-w-0">
        <div className="text-[0.7rem] uppercase tracking-widest text-white/35 font-mono">Task{it.kind === "extra" ? " · ad-hoc" : ""}</div>
        <div className="text-sm font-medium text-white whitespace-pre-wrap">{it.task || it.work || "(untitled)"}</div>
        {it.reason && <div className="text-xs text-amber-300/80 mt-0.5 italic">“{it.reason}”</div>}
      </div>
      <span className={`justify-self-start text-xs uppercase tracking-widest font-mono rounded-full border px-2 py-0.5 ${STATUS_COLORS[it.status] || STATUS_COLORS.not_started}`}>
        {statusLabel(it.status)}
      </span>
      <span className="text-xs text-sky-300 font-mono min-w-[3rem]">{it.hours || "—"}</span>
    </li>
  );
}

function Pill({ tone, label }) {
  const map = {
    emerald: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
    amber: "text-amber-300 bg-amber-500/10 border-amber-500/30",
    sky: "text-sky-300 bg-sky-500/10 border-sky-500/30",
    violet: "text-cyan-300 bg-cyan-500/10 border-cyan-500/30",
  };
  return (
    <span className={`text-xs font-mono px-2 py-1 rounded-full border ${map[tone] || map.sky}`}>{label}</span>
  );
}
